import React, { useState } from 'react';
import {
  NativeSyntheticEvent,
  TextInputSubmitEditingEventData,
  View,
} from 'react-native';
import { styled } from 'dripsy';
import { useTranslation } from 'react-i18next';

import { IS_WEB } from '@utils';
import { BaseText } from '../common/BaseText';
import { TextInput } from '../common/TextInput';

export const SCREEN_TOP_BAR_HEIGHT = 51;

const Container = styled(View)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: SCREEN_TOP_BAR_HEIGHT,
  bg: '$background0',
  px: ['$6', '$7', '5%'],
});

const SearchInputContainer = styled(View)({
  display: 'flex',
  width: ['100%', 266],
});

const Text = styled(BaseText)({
  color: '$heading800',
  fontWeight: 'semi',
  variant: IS_WEB ? 'text.xbig' : 'text.xxlarge',
});

export interface IScreenTopBarProps {
  title?: string;
  isSearchVisible?: boolean;
  initSearchText?: string;
  withSearchAutofocus?: boolean;
  onSearchChange?(text: string): void;
  onSearchSubmit?(text: string): void;
}

export function ScreenTopBar({
  title,
  isSearchVisible = true,
  withSearchAutofocus = false,
  initSearchText = '',
  onSearchChange,
  onSearchSubmit,
}: IScreenTopBarProps) {
  const [searchText, setSearchText] = useState(initSearchText);
  const { t } = useTranslation();

  function onSearchChangeInner(text: string) {
    setSearchText(text);
    onSearchChange?.(text);
  }

  function onSearchSubmitInner(
    e: NativeSyntheticEvent<TextInputSubmitEditingEventData>,
  ) {
    const { text } = e.nativeEvent;

    if (text.trim()) {
      onSearchSubmit?.(text);
    }
  }

  // TODO: find a way to measure text width and make text into two lines with scaling
  // When text length is more than 20 we do not adjust the font size to fit the container
  // because in this case text will be too small, we just clip the text at the end
  const adjustsFontSizeToFit = (title?.length ?? 0) <= 20;

  return (
    <Container>
      {!!title && (
        <Text
          adjustsFontSizeToFit={adjustsFontSizeToFit}
          numberOfLines={1}
        >
          {title}
        </Text>
      )}

      {isSearchVisible && (
        <SearchInputContainer>
          <TextInput
            hasClearButton
            value={searchText}
            onChangeText={onSearchChangeInner}
            onSubmitEditing={onSearchSubmitInner}
            placeholder={t('common.screenTopBar.search')}
            iconRight="search-light"
            autoFocus={withSearchAutofocus}
          />
        </SearchInputContainer>
      )}
    </Container>
  );
}
